import React, { useState, useEffect } from 'react';
import { 
    Row, 
    Col, 
    Card, 
    Button, 
    Spinner, 
    Badge, 
    Modal, 
    Form,
    Alert,
    InputGroup,
    Dropdown,
    Nav,
    ListGroup,
    OverlayTrigger,
    Tooltip,
    ProgressBar
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUserTie, 
    faPlus, 
    faEdit, 
    faPause, 
    faPlay,
    faTrash,
    faRobot,
    faUsers,
    faTimes,
    faCheck,
    faExclamationTriangle,
    faDatabase,
    faSync,
    faChartLine,
    faCog,
    faBrain,
    faLightbulb,
    faInfoCircle,
    faSave,
    faFire,
    faHeart,
    faGraduationCap,
    faHandshake,
    faEye,
    faCopy,
    faChartBar,
    faDollarSign,
    faPercent,
    faClock,
    faComment,
    faAward,
    faThLarge,
    faTools
} from '@fortawesome/free-solid-svg-icons';
import { 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip as RechartsTooltip, 
    ResponsiveContainer,
    BarChart,
    Bar
} from 'recharts';
import { getHatateToken } from '../../helpers/utils';

// ==================== 📋 INTERFACES PRINCIPALES ====================

interface Agent {
    id: string;
    name: string;
    age: number;
    gender: 'male' | 'female' | 'neutral';
    personality_type: 'aggressive' | 'consultive' | 'educational' | 'friendly';
    bio: string;
    specialties: string[];
    prompt_template: string;
    avatar_url?: string;
    emoji: string;
    is_active: boolean;
    max_concurrent_contacts: number;
    performance_metrics?: {
        total_conversations: number;
        closed_sales: number;
        revenue: number;
        conversion_rate: number;
        avg_response_time: number;
        customer_satisfaction: number;
    };
    current_metrics?: {
        current_contacts: number;
        active_conversations: number;
        monthly_sales: number;
        monthly_revenue: number;
        capacity_usage: number;
    };
    created_at: string;
    updated_at: string;
}

interface AgentFormData {
    name: string;
    age: number;
    gender: 'male' | 'female' | 'neutral';
    personality_type: 'aggressive' | 'consultive' | 'educational' | 'friendly';
    bio: string;
    specialties: string[];
    prompt_template: string;
    avatar_url: string;
    emoji: string;
    is_active: boolean;
    max_concurrent_contacts: number;
}

interface AgentsTabProps {
    language: 'es' | 'en';
    onAgentsChange?: (agents: Agent[]) => void;
}

// ==================== 🎨 CONFIGURACIÓN DE PERSONALIDADES ====================

const PERSONALITY_CONFIG = {
    aggressive: {
        icon: faFire,
        color: '#dc3545', // Rojo
        bgColor: '#fff5f5',
        name: { es: 'Agresivo', en: 'Aggressive' },
        description: { 
            es: 'Directo, persuasivo, enfoque en cierre rápido',
            en: 'Direct, persuasive, quick closing focus'
        },
        traits: { 
            es: ['Directo', 'Urgente', 'Persuasivo', 'Decisivo'],
            en: ['Direct', 'Urgent', 'Persuasive', 'Decisive']
        }
    },
    consultive: {
        icon: faLightbulb,
        color: '#0d6efd', // Azul
        bgColor: '#f0f8ff',
        name: { es: 'Consultivo', en: 'Consultive' },
        description: { 
            es: 'Analítico, hace preguntas, construye confianza',
            en: 'Analytical, asks questions, builds trust'
        },
        traits: { 
            es: ['Analítico', 'Estratégico', 'Confiable', 'Detallado'],
            en: ['Analytical', 'Strategic', 'Trustworthy', 'Detailed']
        }
    },
    educational: {
        icon: faGraduationCap,
        color: '#198754', // Verde
        bgColor: '#f0fff4',
        name: { es: 'Educativo', en: 'Educational' },
        description: { 
            es: 'Informativo, explica beneficios, guía al cliente',
            en: 'Informative, explains benefits, guides client'
        },
        traits: { 
            es: ['Informativo', 'Paciente', 'Claro', 'Útil'],
            en: ['Informative', 'Patient', 'Clear', 'Helpful']
        }
    },
    friendly: {
        icon: faHeart,
        color: '#fd7e14', // Naranja
        bgColor: '#fff8f0',
        name: { es: 'Amigable', en: 'Friendly' },
        description: { 
            es: 'Casual, empático, relación a largo plazo',
            en: 'Casual, empathetic, long-term relationship'
        },
        traits: { 
            es: ['Empático', 'Casual', 'Cálido', 'Relacional'],
            en: ['Empathetic', 'Casual', 'Warm', 'Relational']
        }
    }
};

// ==================== 🎨 ESTILOS CSS ====================
const customStyles = `
  .agents-tab .agent-card {
    transition: all 0.3s ease !important;
    border: 1px solid #e9ecef !important;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  }

  .agents-tab .agent-card:hover {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
    transform: translateY(-2px) !important;
    z-index: 1 !important;
  }

  .agents-tab .personality-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;
  }

  .agents-tab .personality-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .agents-tab .personality-card.selected {
    border-color: var(--bs-primary);
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
  }

  .agents-tab .metric-card {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: none;
    transition: all 0.3s ease;
  }

  .agents-tab .metric-card:hover {
    background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
  }

  .agents-tab .prompt-editor {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .agents-tab .variable-tag {
    background: #e3f2fd;
    color: #1976d2;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    .agents-tab .agent-card:hover {
      transform: none;
    }
  }
`;

// ==================== 🔧 UTILIDADES ====================
const getAuthHeaders = () => {
    const token = getHatateToken();
    return {
        'Content-Type': 'application/json',
        'Authorization': token || 'Bearer default-token',
        'x-api-key': 'anubis-sales-bot-api-key'
    };
};

// ==================== 🎯 TEMPLATES DE PROMPT PREDEFINIDOS ====================
const PROMPT_TEMPLATES = {
    aggressive: {
        es: `Eres {agent_name}, un vendedor directo y persuasivo. Tu objetivo es cerrar la venta de {product_name} por {product_price}.

Características:
- Sé directo y ve al grano
- Crea urgencia ("oferta limitada", "solo hoy")
- Usa técnicas de presión suave pero efectiva
- Enfócate en el ROI y beneficios inmediatos

Variables disponibles: {client_name}, {product_name}, {product_price}, {agent_name}

Siempre mantén un tono profesional pero decidido.`,
        en: `You are {agent_name}, a direct and persuasive salesperson. Your goal is to close the sale of {product_name} for {product_price}.

Characteristics:
- Be direct and get to the point
- Create urgency ("limited offer", "today only")
- Use gentle but effective pressure techniques
- Focus on ROI and immediate benefits

Available variables: {client_name}, {product_name}, {product_price}, {agent_name}

Always maintain a professional but decisive tone.`
    },
    consultive: {
        es: `Eres {agent_name}, un consultor de ventas estratégico especializado en {product_name}.

Tu enfoque:
- Haz preguntas para entender las necesidades del cliente
- Analiza su situación actual y desafíos
- Presenta soluciones personalizadas
- Construye confianza a través del conocimiento

Proceso de venta:
1. Descubrimiento de necesidades
2. Análisis de la situación
3. Presentación de solución
4. Manejo de objeciones
5. Cierre consultivo

Variables: {client_name}, {product_name}, {product_price}, {agent_name}`,
        en: `You are {agent_name}, a strategic sales consultant specialized in {product_name}.

Your approach:
- Ask questions to understand client needs
- Analyze their current situation and challenges  
- Present personalized solutions
- Build trust through knowledge

Sales process:
1. Needs discovery
2. Situation analysis
3. Solution presentation
4. Objection handling
5. Consultive closing

Variables: {client_name}, {product_name}, {product_price}, {agent_name}`
    },
    educational: {
        es: `Eres {agent_name}, un experto educativo en {product_name}. Tu misión es informar y guiar al cliente hacia la mejor decisión.

Metodología:
- Explica conceptos de manera clara y simple
- Usa ejemplos y casos de estudio
- Proporciona información valiosa gratuita
- Posiciona el producto como solución educada

Estructura de conversación:
1. Educación sobre el problema
2. Explicación de soluciones disponibles
3. Demostración de beneficios
4. Guía hacia la decisión correcta

Variables: {client_name}, {product_name}, {product_price}, {agent_name}`,
        en: `You are {agent_name}, an educational expert in {product_name}. Your mission is to inform and guide the client toward the best decision.

Methodology:
- Explain concepts clearly and simply
- Use examples and case studies
- Provide valuable free information
- Position product as educated solution

Conversation structure:
1. Education about the problem
2. Explanation of available solutions
3. Demonstration of benefits
4. Guidance toward correct decision

Variables: {client_name}, {product_name}, {product_price}, {agent_name}`
    },
    friendly: {
        es: `¡Hola! Soy {agent_name}, y me encanta ayudar a personas como {client_name} a encontrar soluciones que realmente funcionen.

Mi estilo:
- Conversación natural y relajada
- Enfoque en la relación a largo plazo
- Empático y comprensivo
- Genuinamente interesado en ayudar

Valores:
- Honestidad por encima de todo
- Paciencia para encontrar la solución correcta
- Celebrar los éxitos del cliente
- Estar disponible después de la venta

¿Cómo puedo ayudarte hoy con {product_name}?

Variables: {client_name}, {product_name}, {product_price}, {agent_name}`,
        en: `Hi! I'm {agent_name}, and I love helping people like {client_name} find solutions that really work.

My style:
- Natural and relaxed conversation
- Focus on long-term relationship
- Empathetic and understanding
- Genuinely interested in helping

Values:
- Honesty above all
- Patience to find the right solution
- Celebrate client successes
- Available after the sale

How can I help you today with {product_name}?

Variables: {client_name}, {product_name}, {product_price}, {agent_name}`
    }
};

// ==================== 🔄 HOOK PERSONALIZADO ====================
const useSellerBotAgents = () => {
    const [agents, setAgents] = useState<Agent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchAgents = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // CAMBIO: Usar endpoint de profiles existente
            const baseUrl = "https://backend.anubisai.net";
            const response = await fetch(`${baseUrl}/api/sellerbot/profiles`, {
                method: 'GET',
                headers: getAuthHeaders(),
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            if (data.success && data.data?.profiles) {
                // CAMBIO: Mapear perfiles a formato de agentes
                const mappedAgents = data.data.profiles.map((profile: any) => ({
                    id: profile.id,
                    name: profile.name,
                    age: 25, // Default ya que no está en profiles
                    gender: profile.gender || 'neutral',
                    personality_type: profile.personality,
                    bio: profile.description,
                    specialties: [profile.specialty], // Convertir string a array
                    prompt_template: `Soy ${profile.name}, un agente ${profile.personality} especializado en ${profile.specialty}`,
                    avatar_url: '',
                    emoji: '🤖',
                    is_active: profile.isActive,
                    max_concurrent_contacts: 10, // Default
                    performance_metrics: {
                        total_conversations: 0,
                        closed_sales: profile.conversionsToday || 0,
                        revenue: 0,
                        conversion_rate: profile.successRate || 0,
                        avg_response_time: parseFloat(profile.avgResponseTime?.replace('s', '') || '0'),
                        customer_satisfaction: 4.0
                    },
                    current_metrics: {
                        current_contacts: profile.leadsContacted || 0,
                        active_conversations: 0,
                        monthly_sales: profile.conversionsToday || 0,
                        monthly_revenue: 0,
                        capacity_usage: 0
                    },
                    created_at: profile.createdAt,
                    updated_at: profile.createdAt
                }));
                
                setAgents(mappedAgents);
            } else {
                throw new Error(data.message || 'Error al cargar agentes');
            }
        } catch (err: any) {
            console.error('❌ Error cargando agentes:', err);
            setError(err.message || 'Error al cargar agentes');
            setAgents([]);
        } finally {
            setLoading(false);
        }
    };

    const createAgent = async (agentData: AgentFormData) => {
        try {
            // CAMBIO: Mapear datos de agente a formato de perfil
            const profileData = {
                name: agentData.name,
                personality: agentData.personality_type,
                gender: agentData.gender,
                age_range: `${agentData.age} años`,
                specialty: agentData.specialties.join(', ') || 'General',
                language: 'es',
                description: agentData.bio,
                is_active: agentData.is_active
            };

            const baseUrl = "https://backend.anubisai.net";
            const response = await fetch(`${baseUrl}/api/sellerbot/profiles`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(profileData)
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            if (data.success) {
                await fetchAgents();
                return data.data;
            } else {
                throw new Error(data.message || 'Error al crear agente');
            }
        } catch (err: any) {
            console.error('❌ Error creando agente:', err);
            throw err;
        }
    };

    const updateAgent = async (agentId: string, agentData: Partial<AgentFormData>) => {
        try {
            // CAMBIO: Mapear datos de agente a formato de perfil
            const profileData: any = {};
            
            if (agentData.name) profileData.name = agentData.name;
            if (agentData.personality_type) profileData.personality = agentData.personality_type;
            if (agentData.gender) profileData.gender = agentData.gender;
            if (agentData.age) profileData.age_range = `${agentData.age} años`;
            if (agentData.specialties) profileData.specialty = agentData.specialties.join(', ');
            if (agentData.bio) profileData.description = agentData.bio;
            if (agentData.is_active !== undefined) profileData.is_active = agentData.is_active;

            const baseUrl = "https://backend.anubisai.net";
            const response = await fetch(`${baseUrl}/api/sellerbot/profiles/${agentId}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(profileData)
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            if (data.success) {
                await fetchAgents();
                return data.data;
            } else {
                throw new Error(data.message || 'Error al actualizar agente');
            }
        } catch (err: any) {
            console.error('❌ Error actualizando agente:', err);
            throw err;
        }
    };

    const deleteAgent = async (agentId: string) => {
        try {
            const baseUrl = "https://backend.anubisai.net";
            const response = await fetch(`${baseUrl}/api/sellerbot/profiles/${agentId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            if (data.success) {
                await fetchAgents();
                return agentId;
            } else {
                throw new Error(data.message || 'Error al eliminar agente');
            }
        } catch (err: any) {
            console.error('❌ Error eliminando agente:', err);
            throw err;
        }
    };

    const toggleAgentStatus = async (agentId: string) => {
        const agent = agents.find(a => a.id === agentId);
        if (agent) {
            await updateAgent(agentId, { is_active: !agent.is_active });
        }
    };

    useEffect(() => {
        fetchAgents();
    }, []);

    return {
        agents,
        loading,
        error,
        refetch: fetchAgents,
        createAgent,
        updateAgent,
        deleteAgent,
        toggleAgentStatus
    };
};

// ==================== 🎯 COMPONENTE PRINCIPAL ====================
const AgentsTab: React.FC<AgentsTabProps> = ({ language, onAgentsChange }) => {
    const {
        agents,
        loading: agentsLoading,
        error: agentsError,
        refetch: refetchAgents,
        createAgent,
        updateAgent,
        deleteAgent,
        toggleAgentStatus
    } = useSellerBotAgents();

    // Estados locales
    const [showAgentModal, setShowAgentModal] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
    const [agentFormData, setAgentFormData] = useState<AgentFormData>({
        name: '',
        age: 25,
        gender: 'neutral',
        personality_type: 'friendly',
        bio: '',
        specialties: [],
        prompt_template: '',
        avatar_url: '',
        emoji: '🤖',
        is_active: true,
        max_concurrent_contacts: 10
    });
    const [activeTab, setActiveTab] = useState('basic');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
    const [newSpecialty, setNewSpecialty] = useState('');

    // Notificar cambios al componente padre
    useEffect(() => {
        if (onAgentsChange) {
            onAgentsChange(agents);
        }
    }, [agents, onAgentsChange]);

    // ==================== 📝 FUNCIONES DE FORMULARIO ====================
    
    const handleShowAgentModal = (agent?: Agent) => {
        if (agent) {
            setSelectedAgent(agent);
            setAgentFormData({
                name: agent.name,
                age: agent.age,
                gender: agent.gender,
                personality_type: agent.personality_type,
                bio: agent.bio,
                specialties: agent.specialties,
                prompt_template: agent.prompt_template,
                avatar_url: agent.avatar_url || '',
                emoji: agent.emoji,
                is_active: agent.is_active,
                max_concurrent_contacts: agent.max_concurrent_contacts
            });
        } else {
            setSelectedAgent(null);
            setAgentFormData({
                name: '',
                age: 25,
                gender: 'neutral',
                personality_type: 'friendly',
                bio: '',
                specialties: [],
                prompt_template: PROMPT_TEMPLATES.friendly[language],
                avatar_url: '',
                emoji: '🤖',
                is_active: true,
                max_concurrent_contacts: 10
            });
        }
        setShowAgentModal(true);
        setFormError(null);
        setActiveTab('basic');
    };

    const handleSaveAgent = async () => {
        try {
            setFormLoading(true);
            setFormError(null);

            if (selectedAgent) {
                await updateAgent(selectedAgent.id, agentFormData);
            } else {
                await createAgent(agentFormData);
            }

            setShowAgentModal(false);
            setSelectedAgent(null);
        } catch (err: any) {
            setFormError(err.message || 'Error al guardar agente');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteAgent = async () => {
        if (!agentToDelete) return;

        try {
            await deleteAgent(agentToDelete.id);
            setShowDeleteConfirm(false);
            setAgentToDelete(null);
        } catch (err: any) {
            console.error('Error eliminando agente:', err);
        }
    };

    const addSpecialty = () => {
        if (newSpecialty.trim()) {
            setAgentFormData(prev => ({
                ...prev,
                specialties: [...prev.specialties, newSpecialty.trim()]
            }));
            setNewSpecialty('');
        }
    };

    const removeSpecialty = (index: number) => {
        setAgentFormData(prev => ({
            ...prev,
            specialties: prev.specialties.filter((_, i) => i !== index)
        }));
    };

    const handlePersonalityChange = (personality: string) => {
        setAgentFormData(prev => ({
            ...prev,
            personality_type: personality as any,
            prompt_template: PROMPT_TEMPLATES[personality as keyof typeof PROMPT_TEMPLATES][language]
        }));
    };

    // ==================== 🎨 FUNCIONES DE UTILIDAD ====================
    
    const getText = (key: string) => {
        const texts: Record<string, Record<string, string>> = {
            es: {
                title: 'Gestión de Agentes IA',
                addAgent: 'Crear Agente',
                loading: 'Cargando...',
                error: 'Error',
                noAgents: 'No hay agentes creados',
                active: 'Activo',
                inactive: 'Inactivo',
                edit: 'Editar',
                delete: 'Eliminar',
                toggle: 'Cambiar Estado',
                clone: 'Clonar',
                name: 'Nombre',
                age: 'Edad',
                gender: 'Género',
                personality: 'Personalidad',
                bio: 'Biografía',
                specialties: 'Especialidades',
                prompt: 'Template de Prompt',
                emoji: 'Emoji',
                maxContacts: 'Contactos Máximos',
                save: 'Guardar',
                cancel: 'Cancelar',
                confirmDelete: 'Confirmar eliminación',
                deleteMessage: '¿Estás seguro de que quieres eliminar este agente?',
                basicInfo: 'Información Básica',
                personality_config: 'Configuración de Personalidad',
                promptConfig: 'Configuración de Prompts',
                metrics: 'Métricas'
            },
            en: {
                title: 'AI Agents Management',
                addAgent: 'Create Agent',
                loading: 'Loading...',
                error: 'Error',
                noAgents: 'No agents created',
                active: 'Active',
                inactive: 'Inactive',
                edit: 'Edit',
                delete: 'Delete',
                toggle: 'Toggle Status',
                clone: 'Clone',
                name: 'Name',
                age: 'Age', 
                gender: 'Gender',
                personality: 'Personality',
                bio: 'Biography',
                specialties: 'Specialties',
                prompt: 'Prompt Template',
                emoji: 'Emoji',
                maxContacts: 'Max Contacts',
                save: 'Save',
                cancel: 'Cancel',
                confirmDelete: 'Confirm deletion',
                deleteMessage: 'Are you sure you want to delete this agent?',
                basicInfo: 'Basic Information',
                personality_config: 'Personality Configuration',
                promptConfig: 'Prompt Configuration',
                metrics: 'Metrics'
            }
        };
        return texts[language]?.[key] || key;
    };

    const getPersonalityConfig = (personality: string) => {
        return PERSONALITY_CONFIG[personality as keyof typeof PERSONALITY_CONFIG];
    };

    // ==================== 🎯 RENDERIZADO PRINCIPAL ====================
    
    if (agentsLoading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" />
                <p className="mt-2">{getText('loading')}</p>
            </div>
        );
    }

    if (agentsError) {
        return (
            <Alert variant="danger" className="m-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                {getText('error')}: {agentsError}
            </Alert>
        );
    }

    return (
        <div className="agents-tab">
            <style>{customStyles}</style>
            
            {/* Header */}
            <Row className="mb-3">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4>
                            <FontAwesomeIcon icon={faUserTie} className="me-2" />
                            {getText('title')} ({agents.length})
                        </h4>
                        <div>
                            <Button 
                                variant="primary" 
                                onClick={() => handleShowAgentModal()}
                                className="me-2"
                            >
                                <FontAwesomeIcon icon={faPlus} className="me-1" />
                                {getText('addAgent')}
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => refetchAgents()} 
                                size="sm"
                            >
                                <FontAwesomeIcon icon={faSync} />
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Lista de Agentes */}
            {agents.length === 0 ? (
                <Card>
                    <Card.Body className="text-center py-4">
                        <FontAwesomeIcon icon={faUserTie} size="3x" className="text-muted mb-3" />
                        <h5 className="text-muted">{getText('noAgents')}</h5>
                        <Button variant="primary" onClick={() => handleShowAgentModal()}>
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            {getText('addAgent')}
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <Row>
                    {agents.map((agent) => {
                        const personalityConfig = getPersonalityConfig(agent.personality_type);
                        const metrics = agent.performance_metrics || {
                            total_conversations: 0,
                            closed_sales: 0,
                            revenue: 0,
                            conversion_rate: 0,
                            avg_response_time: 0,
                            customer_satisfaction: 0
                        };
                        const currentMetrics = agent.current_metrics || {
                            current_contacts: 0,
                            active_conversations: 0,
                            monthly_sales: 0,
                            monthly_revenue: 0,
                            capacity_usage: 0
                        };

                        return (
                            <Col key={agent.id} md={6} lg={4} className="mb-3">
                                <Card className="h-100 agent-card">
                                    <Card.Header 
                                        className="d-flex justify-content-between align-items-center"
                                        style={{ 
                                            backgroundColor: personalityConfig.bgColor,
                                            borderBottom: `2px solid ${personalityConfig.color}`
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                style={{ 
                                                    width: '40px', 
                                                    height: '40px',
                                                    backgroundColor: personalityConfig.color + '20',
                                                    border: `2px solid ${personalityConfig.color}`
                                                }}
                                            >
                                                <span style={{ fontSize: '1.2rem' }}>{agent.emoji}</span>
                                            </div>
                                            <div>
                                                <h6 className="mb-0">{agent.name}</h6>
                                                <small style={{ color: personalityConfig.color }}>
                                                    <FontAwesomeIcon icon={personalityConfig.icon} className="me-1" />
                                                    {personalityConfig.name[language]}
                                                </small>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <Badge 
                                                bg={agent.is_active ? 'success' : 'secondary'}
                                                className="me-2"
                                            >
                                                {agent.is_active ? getText('active') : getText('inactive')}
                                            </Badge>
                                            <Dropdown>
                                                <Dropdown.Toggle variant="outline-secondary" size="sm">
                                                    <FontAwesomeIcon icon={faCog} />
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => handleShowAgentModal(agent)}>
                                                        <FontAwesomeIcon icon={faEdit} className="me-1" />
                                                        {getText('edit')}
                                                    </Dropdown.Item>
                                                    <Dropdown.Item onClick={() => toggleAgentStatus(agent.id)}>
                                                        <FontAwesomeIcon icon={agent.is_active ? faPause : faPlay} className="me-1" />
                                                        {getText('toggle')}
                                                    </Dropdown.Item>
                                                    <Dropdown.Item onClick={() => handleShowAgentModal({...agent, name: `${agent.name} (Copia)`})}>
                                                        <FontAwesomeIcon icon={faCopy} className="me-1" />
                                                        {getText('clone')}
                                                    </Dropdown.Item>
                                                    <Dropdown.Divider />
                                                    <Dropdown.Item 
                                                        className="text-danger"
                                                        onClick={() => {
                                                            setAgentToDelete(agent);
                                                            setShowDeleteConfirm(true);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} className="me-1" />
                                                        {getText('delete')}
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    </Card.Header>

                                    <Card.Body>
                                        {/* Información básica */}
                                        <div className="mb-3">
                                            <p className="text-muted small mb-2">
                                                {agent.bio.length > 80 
                                                    ? `${agent.bio.substring(0, 80)}...` 
                                                    : agent.bio
                                                }
                                            </p>
                                            
                                            {/* Especialidades */}
                                            {agent.specialties.length > 0 && (
                                                <div className="mb-2">
                                                    {agent.specialties.slice(0, 3).map((specialty, index) => (
                                                        <Badge key={index} bg="light" text="dark" className="me-1 mb-1 small">
                                                            {specialty}
                                                        </Badge>
                                                    ))}
                                                    {agent.specialties.length > 3 && (
                                                        <Badge bg="secondary" className="small">
                                                            +{agent.specialties.length - 3}
                                                        </Badge>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Métricas principales */}
                                        <Row className="g-2 mb-3">
                                            <Col xs={6}>
                                                <Card className="metric-card p-2 text-center">
                                                    <div className="fw-bold text-primary">{currentMetrics.active_conversations}</div>
                                                    <div className="small text-muted">
                                                        {language === 'es' ? 'Conversaciones' : 'Conversations'}
                                                    </div>
                                                </Card>
                                            </Col>
                                            <Col xs={6}>
                                                <Card className="metric-card p-2 text-center">
                                                    <div className="fw-bold text-success">{currentMetrics.monthly_sales}</div>
                                                    <div className="small text-muted">
                                                        {language === 'es' ? 'Ventas/Mes' : 'Sales/Month'}
                                                    </div>
                                                </Card>
                                            </Col>
                                            <Col xs={6}>
                                                <Card className="metric-card p-2 text-center">
                                                    <div className="fw-bold text-warning">
                                                        {metrics.conversion_rate.toFixed(1)}%
                                                    </div>
                                                    <div className="small text-muted">
                                                        {language === 'es' ? 'Conversión' : 'Conversion'}
                                                    </div>
                                                </Card>
                                            </Col>
                                            <Col xs={6}>
                                                <Card className="metric-card p-2 text-center">
                                                    <div className="fw-bold text-info">
                                                        ${(currentMetrics.monthly_revenue || 0).toLocaleString()}
                                                    </div>
                                                    <div className="small text-muted">
                                                        {language === 'es' ? 'Revenue' : 'Revenue'}
                                                    </div>
                                                </Card>
                                            </Col>
                                        </Row>

                                        {/* Barra de capacidad */}
                                        <div className="mb-2">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <small className="text-muted">
                                                    {language === 'es' ? 'Capacidad:' : 'Capacity:'}
                                                </small>
                                                <small className="fw-semibold">
                                                    {currentMetrics.current_contacts}/{agent.max_concurrent_contacts}
                                                </small>
                                            </div>
                                            <ProgressBar 
                                                now={currentMetrics.capacity_usage} 
                                                variant={
                                                    currentMetrics.capacity_usage >= 90 ? 'danger' :
                                                    currentMetrics.capacity_usage >= 70 ? 'warning' : 'success'
                                                }
                                            />
                                        </div>

                                        {/* Rasgos de personalidad */}
                                        <div className="d-flex flex-wrap gap-1">
                                            {personalityConfig.traits[language].slice(0, 4).map((trait, index) => (
                                                <span 
                                                    key={index}
                                                    className="badge"
                                                    style={{ 
                                                        backgroundColor: personalityConfig.color + '20',
                                                        color: personalityConfig.color,
                                                        fontSize: '0.7rem'
                                                    }}
                                                >
                                                    {trait}
                                                </span>
                                            ))}
                                        </div>
                                    </Card.Body>

                                    <Card.Footer className="bg-transparent small text-muted">
                                        <div className="d-flex justify-content-between">
                                            <span>
                                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                                {language === 'es' ? 'Creado:' : 'Created:'} {new Date(agent.created_at).toLocaleDateString()}
                                            </span>
                                            <span>
                                                <FontAwesomeIcon icon={faUsers} className="me-1" />
                                                {agent.age} {language === 'es' ? 'años' : 'years'}
                                            </span>
                                        </div>
                                    </Card.Footer>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {/* ==================== 🎯 MODAL DE CREACIÓN/EDICIÓN ==================== */}
            <Modal show={showAgentModal} onHide={() => setShowAgentModal(false)} scrollable size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faUserTie} className="me-2" />
                        {selectedAgent ? getText('edit') + ' ' + selectedAgent.name : getText('addAgent')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && (
                        <Alert variant="danger" className="mb-3">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            {formError}
                        </Alert>
                    )}

                    {/* Navegación por tabs */}
                    <Nav variant="tabs" className="mb-3">
                        <Nav.Item>
                            <Nav.Link 
                                active={activeTab === 'basic'} 
                                onClick={() => setActiveTab('basic')}
                            >
                                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                {getText('basicInfo')}
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                active={activeTab === 'personality'} 
                                onClick={() => setActiveTab('personality')}
                            >
                                <FontAwesomeIcon icon={faBrain} className="me-1" />
                                {getText('personality_config')}
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                active={activeTab === 'prompt'} 
                                onClick={() => setActiveTab('prompt')}
                            >
                                <FontAwesomeIcon icon={faComment} className="me-1" />
                                {getText('promptConfig')}
                            </Nav.Link>
                        </Nav.Item>
                        {selectedAgent && (
                            <Nav.Item>
                                <Nav.Link 
                                    active={activeTab === 'metrics'} 
                                    onClick={() => setActiveTab('metrics')}
                                >
                                    <FontAwesomeIcon icon={faChartBar} className="me-1" />
                                    {getText('metrics')}
                                </Nav.Link>
                            </Nav.Item>
                        )}
                    </Nav>

                    {/* TAB: Información Básica */}
                    {activeTab === 'basic' && (
                        <Form>
                            <Row>
                                <Col md={8}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('name')}</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                {agentFormData.emoji}
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                value={agentFormData.name}
                                                onChange={(e) => setAgentFormData(prev => ({
                                                    ...prev,
                                                    name: e.target.value
                                                }))}
                                                placeholder="Ej: Sarah la Consultora"
                                                required
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('emoji')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={agentFormData.emoji}
                                            onChange={(e) => setAgentFormData(prev => ({
                                                ...prev,
                                                emoji: e.target.value
                                            }))}
                                            placeholder="🤖"
                                            maxLength={2}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('age')}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={agentFormData.age}
                                            onChange={(e) => setAgentFormData(prev => ({
                                                ...prev,
                                                age: parseInt(e.target.value) || 25
                                            }))}
                                            min={18}
                                            max={65}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('gender')}</Form.Label>
                                        <Form.Select
                                            value={agentFormData.gender}
                                            onChange={(e) => setAgentFormData(prev => ({
                                                ...prev,
                                                gender: e.target.value as any
                                            }))}
                                        >
                                            <option value="neutral">{language === 'es' ? 'Neutral' : 'Neutral'}</option>
                                            <option value="male">{language === 'es' ? 'Masculino' : 'Male'}</option>
                                            <option value="female">{language === 'es' ? 'Femenino' : 'Female'}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>{getText('bio')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={agentFormData.bio}
                                    onChange={(e) => setAgentFormData(prev => ({
                                        ...prev,
                                        bio: e.target.value
                                    }))}
                                    placeholder={language === 'es' 
                                        ? 'Describe brevemente al agente, su experiencia y estilo de trabajo...'
                                        : 'Briefly describe the agent, their experience and work style...'
                                    }
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>{getText('specialties')}</Form.Label>
                                <div className="mb-2">
                                    {agentFormData.specialties.map((specialty, index) => (
                                        <Badge 
                                            key={index} 
                                            bg="primary" 
                                            className="me-1 mb-1 position-relative"
                                            style={{ paddingRight: '25px' }}
                                        >
                                            {specialty}
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="position-absolute top-0 end-0 p-0 text-white"
                                                style={{ fontSize: '0.7rem', lineHeight: '1' }}
                                                onClick={() => removeSpecialty(index)}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </Button>
                                        </Badge>
                                    ))}
                                </div>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        value={newSpecialty}
                                        onChange={(e) => setNewSpecialty(e.target.value)}
                                        placeholder={language === 'es' ? 'Ej: SaaS, E-commerce, Inmobiliaria' : 'Ex: SaaS, E-commerce, Real Estate'}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addSpecialty();
                                            }
                                        }}
                                    />
                                    <Button 
                                        variant="outline-primary" 
                                        onClick={addSpecialty}
                                        disabled={!newSpecialty.trim()}
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </Button>
                                </InputGroup>
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('maxContacts')}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={agentFormData.max_concurrent_contacts}
                                            onChange={(e) => setAgentFormData(prev => ({
                                                ...prev,
                                                max_concurrent_contacts: parseInt(e.target.value) || 10
                                            }))}
                                            min={1}
                                            max={100}
                                        />
                                        <Form.Text className="text-muted">
                                            {language === 'es' 
                                                ? 'Máximo de contactos que puede manejar simultáneamente'
                                                : 'Maximum contacts that can handle simultaneously'
                                            }
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{language === 'es' ? 'Estado Inicial' : 'Initial Status'}</Form.Label>
                                        <div className="mt-2">
                                            <Form.Check
                                                type="switch"
                                                id="agent-active-switch"
                                                label={agentFormData.is_active ? getText('active') : getText('inactive')}
                                                checked={agentFormData.is_active}
                                                onChange={(e) => setAgentFormData(prev => ({
                                                    ...prev,
                                                    is_active: e.target.checked
                                                }))}
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    )}

{/* TAB: Configuración de Personalidad */}
                    {activeTab === 'personality' && (
                        <div>
                            <h6 className="mb-3">
                                <FontAwesomeIcon icon={faBrain} className="me-2" />
                                {language === 'es' ? 'Selecciona la Personalidad del Agente' : 'Select Agent Personality'}
                            </h6>
                            
                            <Row className="g-3">
                                {Object.entries(PERSONALITY_CONFIG).map(([key, config]) => (
                                    <Col xs={12} md={6} key={key}>
                                        <Card 
                                            className={`personality-card h-100 ${agentFormData.personality_type === key ? 'selected' : ''}`}
                                            onClick={() => handlePersonalityChange(key)}
                                            style={{
                                                backgroundColor: agentFormData.personality_type === key 
                                                    ? config.bgColor 
                                                    : '#fff',
                                                borderColor: agentFormData.personality_type === key 
                                                    ? config.color 
                                                    : '#dee2e6'
                                            }}
                                        >
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center mb-2">
                                                    <div 
                                                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                                        style={{ 
                                                            width: '50px', 
                                                            height: '50px',
                                                            backgroundColor: config.color + '20',
                                                            border: `2px solid ${config.color}`
                                                        }}
                                                    >
                                                        <FontAwesomeIcon 
                                                            icon={config.icon} 
                                                            style={{ color: config.color, fontSize: '1.2rem' }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0" style={{ color: config.color }}>
                                                            {config.name[language]}
                                                        </h6>
                                                        <small className="text-muted">
                                                            {config.description[language]}
                                                        </small>
                                                    </div>
                                                </div>
                                                
                                                <div className="d-flex flex-wrap gap-1">
                                                    {config.traits[language].map((trait, index) => (
                                                        <Badge 
                                                            key={index}
                                                            className="small"
                                                            style={{ 
                                                                backgroundColor: config.color + '15',
                                                                color: config.color,
                                                                fontSize: '0.7rem'
                                                            }}
                                                        >
                                                            {trait}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {/* Preview de personalidad seleccionada */}
                            {agentFormData.personality_type && (
                                <Alert 
                                    variant="info" 
                                    className="mt-4"
                                    style={{
                                        backgroundColor: getPersonalityConfig(agentFormData.personality_type).bgColor,
                                        borderColor: getPersonalityConfig(agentFormData.personality_type).color
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon 
                                            icon={getPersonalityConfig(agentFormData.personality_type).icon}
                                            className="me-2"
                                            style={{ color: getPersonalityConfig(agentFormData.personality_type).color }}
                                        />
                                        <div>
                                            <strong>
                                                {language === 'es' ? 'Personalidad Seleccionada: ' : 'Selected Personality: '}
                                                {getPersonalityConfig(agentFormData.personality_type).name[language]}
                                            </strong>
                                            <p className="mb-0 small">
                                                {getPersonalityConfig(agentFormData.personality_type).description[language]}
                                            </p>
                                        </div>
                                    </div>
                                </Alert>
                            )}
                        </div>
                    )}

                    {/* TAB: Configuración de Prompts */}
                    {activeTab === 'prompt' && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">
                                    <FontAwesomeIcon icon={faComment} className="me-2" />
                                    {language === 'es' ? 'Template de Prompt Personalizado' : 'Custom Prompt Template'}
                                </h6>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                        const template = PROMPT_TEMPLATES[agentFormData.personality_type as keyof typeof PROMPT_TEMPLATES];
                                        if (template) {
                                            setAgentFormData(prev => ({
                                                ...prev,
                                                prompt_template: template[language]
                                            }));
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faSync} className="me-1" />
                                    {language === 'es' ? 'Restaurar Template' : 'Restore Template'}
                                </Button>
                            </div>

                            {/* Variables disponibles */}
                            <Alert variant="info" className="mb-3">
                                <h6 className="mb-2">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                    {language === 'es' ? 'Variables Disponibles:' : 'Available Variables:'}
                                </h6>
                                <div className="d-flex flex-wrap gap-2">
                                    {[
                                        '{client_name}',
                                        '{product_name}', 
                                        '{product_price}',
                                        '{agent_name}',
                                        '{company_name}'
                                    ].map(variable => (
                                        <span key={variable} className="variable-tag">
                                            {variable}
                                        </span>
                                    ))}
                                </div>
                                <small className="text-muted mt-2 d-block">
                                    {language === 'es' 
                                        ? 'Estas variables se reemplazarán automáticamente durante las conversaciones'
                                        : 'These variables will be automatically replaced during conversations'
                                    }
                                </small>
                            </Alert>

                            <Form.Group className="mb-3">
                                <Form.Label>{getText('prompt')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={12}
                                    value={agentFormData.prompt_template}
                                    onChange={(e) => setAgentFormData(prev => ({
                                        ...prev,
                                        prompt_template: e.target.value
                                    }))}
                                    className="prompt-editor"
                                    placeholder={language === 'es'
                                        ? 'Escribe el template de prompt que definirá el comportamiento del agente...'
                                        : 'Write the prompt template that will define the agent behavior...'
                                    }
                                />
                                <Form.Text className="text-muted">
                                    {language === 'es'
                                        ? 'Este prompt definirá cómo se comporta el agente en las conversaciones'
                                        : 'This prompt will define how the agent behaves in conversations'
                                    }
                                </Form.Text>
                            </Form.Group>
                        </div>
                    )}

                    {/* TAB: Métricas (solo para agentes existentes) */}
                    {activeTab === 'metrics' && selectedAgent && (
                        <div>
                            <h6 className="mb-3">
                                <FontAwesomeIcon icon={faChartBar} className="me-2" />
                                {language === 'es' ? 'Métricas de Rendimiento' : 'Performance Metrics'}
                            </h6>
                            
                            {/* Métricas principales en cards */}
                            <Row className="g-3 mb-4">
                                <Col md={3} sm={6}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <FontAwesomeIcon icon={faComment} className="text-primary mb-2" size="2x" />
                                            <h4 className="mb-0">{selectedAgent.performance_metrics?.total_conversations || 0}</h4>
                                            <small className="text-muted">
                                                {language === 'es' ? 'Conversaciones Totales' : 'Total Conversations'}
                                            </small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3} sm={6}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <FontAwesomeIcon icon={faHandshake} className="text-success mb-2" size="2x" />
                                            <h4 className="mb-0">{selectedAgent.performance_metrics?.closed_sales || 0}</h4>
                                            <small className="text-muted">
                                                {language === 'es' ? 'Ventas Cerradas' : 'Closed Sales'}
                                            </small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3} sm={6}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <FontAwesomeIcon icon={faPercent} className="text-warning mb-2" size="2x" />
                                            <h4 className="mb-0">{(selectedAgent.performance_metrics?.conversion_rate || 0).toFixed(1)}%</h4>
                                            <small className="text-muted">
                                                {language === 'es' ? 'Tasa de Conversión' : 'Conversion Rate'}
                                            </small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3} sm={6}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <FontAwesomeIcon icon={faDollarSign} className="text-info mb-2" size="2x" />
                                                <h4 className="mb-0">${(selectedAgent.performance_metrics?.revenue || 0).toLocaleString()}</h4>
                                            <small className="text-muted">
                                                {language === 'es' ? 'Revenue Total' : 'Total Revenue'}
                                            </small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Gráfico de rendimiento (simulado para demo) */}
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">
                                        {language === 'es' ? 'Tendencia de Conversiones (Últimos 30 días)' : 'Conversion Trend (Last 30 days)'}
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <LineChart data={[
                                            { day: 1, conversions: 2 },
                                            { day: 5, conversions: 5 },
                                            { day: 10, conversions: 3 },
                                            { day: 15, conversions: 8 },
                                            { day: 20, conversions: 6 },
                                            { day: 25, conversions: 12 },
                                            { day: 30, conversions: 9 }
                                        ]}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="day" />
                                            <YAxis />
                                            <RechartsTooltip />
                                            <Line 
                                                type="monotone" 
                                                dataKey="conversions" 
                                                stroke="#0d6efd" 
                                                strokeWidth={2}
                                                dot={{ fill: '#0d6efd', strokeWidth: 2, r: 4 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Card.Body>
                            </Card>

                            {/* Métricas adicionales */}
                            <Row className="g-3 mt-3">
                                <Col md={6}>
                                    <Card>
                                        <Card.Header>
                                            <h6 className="mb-0">
                                                {language === 'es' ? 'Tiempo de Respuesta Promedio' : 'Average Response Time'}
                                            </h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon icon={faClock} className="text-primary me-2" size="lg" />
                                                <div>
                                                    <h5 className="mb-0">{selectedAgent.performance_metrics?.avg_response_time || 0}s</h5>
                                                    <small className="text-muted">
                                                        {language === 'es' ? 'Segundos por respuesta' : 'Seconds per response'}
                                                    </small>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Header>
                                            <h6 className="mb-0">
                                                {language === 'es' ? 'Satisfacción del Cliente' : 'Customer Satisfaction'}
                                            </h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon icon={faAward} className="text-success me-2" size="lg" />
                                                <div>
                                                    <h5 className="mb-0">{(selectedAgent.performance_metrics?.customer_satisfaction || 0).toFixed(1)}/5.0</h5>
                                                    <small className="text-muted">
                                                        {language === 'es' ? 'Puntuación promedio' : 'Average rating'}
                                                    </small>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAgentModal(false)}>
                        {getText('cancel')}
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveAgent}
                        disabled={formLoading || !agentFormData.name.trim() || !agentFormData.bio.trim()}
                    >
                        {formLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {language === 'es' ? 'Guardando...' : 'Saving...'}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} className="me-1" />
                                {getText('save')}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ==================== 🗑️ MODAL DE CONFIRMACIÓN DE ELIMINACIÓN ==================== */}
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
                        {getText('confirmDelete')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {agentToDelete && (
                        <div>
                            <p>{getText('deleteMessage')}</p>
                            <Card className="bg-light">
                                <Card.Body className="py-2">
                                    <div className="d-flex align-items-center">
                                        <span className="me-2" style={{ fontSize: '1.2rem' }}>
                                            {agentToDelete.emoji}
                                        </span>
                                        <div>
                                            <strong>{agentToDelete.name}</strong>
                                            <br />
                                            <small className="text-muted">
                                                {getPersonalityConfig(agentToDelete.personality_type).name[language]}
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                        {getText('cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleDeleteAgent}>
                        <FontAwesomeIcon icon={faTrash} className="me-1" />
                        {getText('delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AgentsTab;